import { call, put, takeLatest } from "redux-saga/effects";
import { AxiosResponse } from "axios";

import {
    SetAccountDelete,
    setAddBlockedUser,
    setBackgroundColor,
    setBlockedUserData,
    setColorScheme,
    setCountry,
    setDirect,
    setEmail,
    setGender,
    setLanguage,
    setPhone,
    setPhotoTagging,
    setPinTweetId,
    setPrivateProfile,
    setProfileStarted,
    setReadMessage,
    setTokenLoadingStatus,
    setUserData,
    setUserErrorMsg,
    setUserFollowers,
    setUserFollowing,
    setUserLoadingStatus,
    setUsername,
    setVeracityScore
} from "./actionCreators";
import {
    DeleteUserAccountActionInterface,
    FetchPinTweetActionInterface,
    FetchReadMessagesActionInterface,
    FetchSignInActionInterface,
    FetchSignUpActionInterface,
    FetchUserVeracityScoreInterface,
    FollowUserActionInterface,
    ProcessFollowRequestActionInterface,
    ProcessUserToBlocklistActionInterface,
    ProcessUserToMuteListActionInterface,
    UpdateBackgroundColorActionInterface,
    UpdateColorSchemeActionInterface,
    UpdateCountryActionInterface,
    UpdateDirectActionInterface,
    UpdateEmailActionInterface,
    UpdateGenderActionInterface,
    UpdateLanguageActionInterface,
    UpdatePhoneActionInterface,
    UpdatePrivateProfileActionInterface,
    UpdateUserDataActionInterface,
    UpdateUserVeracityScoreInterface,
    UpdateUsernameActionInterface,
    UserActionsType
} from "./contracts/actionTypes";
import { UserApi } from "../../../services/api/user-service/userApi";
import { ChatMessageApi } from "../../../services/api/chat-service/chatMessageApi";
import { UserSettingsApi } from "../../../services/api/user-service/userSettingsApi";
import { AuthenticationResponse, RegistrationStep } from "../../../types/auth";
import { AuthUserResponse, UserProfileResponse } from "../../../types/user";
import {
    setBlocked,
    setFollowRequestToUserProfile,
    setFollowToUserProfile,
    setMuted,
    setUserProfile
} from "../userProfile/actionCreators";
import {
    setBlockedUsersState,
    setFollowRequestToUsers,
    setFollowToUsersState,
    setMutedUsersState
} from "../users/actionCreators";
import {
    setBlockedToTweetState,
    setFollowingTagImageUser,
    setFollowToTweetState,
    setMutedToTweetState
} from "../tweet/actionCreators";
import { setBlockedToTweetsState, setFollowToTweetsState, setMutedToTweetsState } from "../tweets/actionCreators";
import {
    setBlockUsersSearchState,
    setFollowRequestToUsersSearchState,
    setFollowToUsersSearchState
} from "../usersSearch/actionCreators";
import { setBlockUserDetail, setFollowRequestToUserDetail, setFollowToUserDetail } from "../userDetail/actionCreators";
import {
    setBlockedUsersTweetState,
    setFollowToUsersTweetState,
    setMutedUsersTweetState
} from "../userTweets/actionCreators";
import {
    setBlockedNotificationInfo,
    setFollowRequestToNotificationInfo,
    setFollowToNotificationInfo
} from "../notifications/actionCreators";
import { setBlockedUser, setMutedUser } from "../blockedAndMutedUsers/actionCreators";
import { HOME, PROFILE } from "../../../constants/path-constants";
import { UserPhoneResponse } from "./contracts/state";
import {
    setBlockedTweetAdditionalInfo,
    setFollowedTweetAdditionalInfo,
    setMutedTweetAdditionalInfo
} from "../tweetAdditionalInfo/actionCreators";
import { LoadingStatus } from "../../../types/common";
import { TOKEN, USER_ID } from "../../../constants/common-constants";
import { AuthenticationApi } from "../../../services/api/user-service/authenticationApi";
import { BlockUserApi } from "../../../services/api/user-service/blockUserApi";
import { FollowerUserApi } from "../../../services/api/user-service/followerUserApi";
import { MuteUserApi } from "../../../services/api/user-service/muteUserApi";
import { RegistrationApi } from "../../../services/api/user-service/registrationApi";
import {
    setBlockedToListTweetsState,
    setFollowToListTweetsState,
    setMutedToListTweetsState
} from "../list/actionCreators";
import { setCloseModal, setRegistrationStep } from "../authentication/actionCreators";
import { setOpenSnackBar } from "../actionSnackbar/actionCreators";

export function* updateUserDataRequest({ payload }: UpdateUserDataActionInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        const response: AxiosResponse<AuthUserResponse & UserProfileResponse> = yield call(
            UserApi.updateUserProfile,
            payload
        );
        yield put(setUserData(response.data));
        yield put(setUserProfile(response.data));
    } catch (error) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* fetchSignInRequest({ payload }: FetchSignInActionInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        const response: AxiosResponse<AuthenticationResponse> = yield call(AuthenticationApi.login, payload);
        localStorage.setItem(TOKEN, response.data.token);
        localStorage.setItem(USER_ID, response?.data?.user?.id?.toString());
        yield put(setUserData(response.data.user));
        yield put(setBlockedUserData(response?.data?.blockedUsers));
        payload.history.push(HOME);
    } catch (error) {
        if (error?.response?.data?.password || error?.response?.data?.message) {
            const msg = error?.response?.data?.password || error?.response?.data?.message;
            yield put(setUserErrorMsg(msg));
        }
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* fetchSignUpRequest({ payload }: FetchSignUpActionInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        const response: AxiosResponse<AuthenticationResponse> = yield call(RegistrationApi.endRegistration, payload);
        localStorage.setItem(TOKEN, response.data.token);
        localStorage.setItem(USER_ID, response.data.user.id.toString());
        yield put(setUserData(response.data.user));
        yield put(setRegistrationStep(RegistrationStep.STEP_6));
    } catch (error) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* fetchVeracityScore({ payload }: FetchUserVeracityScoreInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        yield call(RegistrationApi.setVeracityScore, payload.veracityScore);
        const userId = localStorage.getItem(USER_ID);
        yield put(setUserLoadingStatus(LoadingStatus.LOADED));
        yield put(setCloseModal());
        // localStorage.removeItem(USER_ID);
        payload.history.push({ pathname: `${PROFILE}/${userId}`, state: { isRegistered: true } });
    } catch (error) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* updateVeracityScore({ payload }: UpdateUserVeracityScoreInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        yield call(RegistrationApi.setVeracityScore, payload);
        yield put(setVeracityScore(payload));
        yield put(setUserLoadingStatus(LoadingStatus.LOADED));
    } catch (error) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* fetchUserDataRequest() {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        const response: AxiosResponse<AuthenticationResponse> = yield call(UserApi.getUserByToken);
        localStorage.setItem(TOKEN, response.data.token);
        localStorage.setItem(USER_ID, response.data.user.id.toString());
        yield put(setBlockedUserData(response?.data?.blockedUsers))
        yield put(setUserData(response.data.user));
    } catch (error) {
        yield put(setTokenLoadingStatus(LoadingStatus.FAILED));
        if (localStorage.getItem(TOKEN) !== null) {
            yield put(setUserLoadingStatus(LoadingStatus.ERROR));
        }
    }
}

export function* processFollowUserRequest({ payload }: FollowUserActionInterface) {
    try {

        if (payload?.followStateForTweet !== undefined) {
            // yield put(setFollowToUserDetail(payload?.followStateForTweet));
            // yield put(setFollowToTweetState(payload?.followStateForTweet));
            // yield put(setFollowingTagImageUser({ userId: payload.userId, isFollowing: payload?.followStateForTweet }));
            // yield put(setFollowToUsersTweetState({ userId: payload.userId, tweetId: payload.tweetId!, isFollower: payload?.followStateForTweet }));
            // yield put(setFollowToListTweetsState({ userId: payload.userId, tweetId: payload.tweetId!, isFollower: payload?.followStateForTweet }));
        }
        const loggedInUserid: string = yield call({context: localStorage, fn: localStorage.getItem}, 'userId');
        const isLoggedinUser = loggedInUserid === payload.userId;
        const { data }: AxiosResponse<boolean> = yield call(FollowerUserApi.processFollow, payload.userId);
        yield put(setFollowToTweetsState({ userId: payload.userId, tweetId: payload.tweetId!, isFollower: data }));
        yield put(setFollowToUsersTweetState({ userId: payload.userId, tweetId: payload.tweetId!, isFollower: data }));
        yield put(setFollowToListTweetsState({ userId: payload.userId, tweetId: payload.tweetId!, isFollower: data }));
        if (isLoggedinUser) yield put(setUserFollowers(data)); else yield put(setUserFollowing(data));
        yield put(setFollowToUserProfile({ userId: payload.userId, isFollower: data, isLoggedinUser }));
        yield put(setFollowToUserDetail(data));
        yield put(setFollowToUsersState({ userId: payload.userId, isFollower: data }));
        yield put(setFollowToUsersSearchState({ userId: payload.userId, isFollower: data }));
        yield put(setFollowToTweetState(data));
        yield put(setFollowToNotificationInfo(data));
        yield put(setFollowedTweetAdditionalInfo(data));
        yield put(setFollowingTagImageUser({ userId: payload.userId, isFollowing: data }));
    } catch (error) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* startUseTwitterRequest() {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        const response: AxiosResponse<boolean> = yield call(UserApi.startUseTwitter);
        yield put(setProfileStarted(response.data));
    } catch (e) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* fetchPinTweetRequest({ payload }: FetchPinTweetActionInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        const response: AxiosResponse<string> = yield call(UserApi.processPinTweet, payload);
        yield put(setPinTweetId(response.data));
    } catch (e) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* fetchReadMessagesRequest({ payload }: FetchReadMessagesActionInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        const item: AxiosResponse<number> = yield call(ChatMessageApi.readChatMessages, payload);
        yield put(setReadMessage(item.data));
    } catch (e) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* updateUsernameRequest({ payload }: UpdateUsernameActionInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        const response: AxiosResponse<string> = yield call(UserSettingsApi.updateUsername, payload);
        yield put(setUsername(response.data));
    } catch (e) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* updateEmailRequest({ payload }: UpdateEmailActionInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        const item: AxiosResponse<AuthenticationResponse> = yield call(UserSettingsApi.updateEmail, payload);
        localStorage.setItem(TOKEN, item.data.token);
        yield put(setEmail(item.data.user.email));
    } catch (e) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* updatePhoneRequest({ payload }: UpdatePhoneActionInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        const { data }: AxiosResponse<UserPhoneResponse> = yield call(UserSettingsApi.updatePhone, payload);
        yield put(setPhone(data));
    } catch (e) {
        if (e?.response?.data?.message) {
            payload.setError?.("phoneNumber", { type: "server", message: e?.response?.data?.message });
        }
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* updateCountryRequest({ payload }: UpdateCountryActionInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        const response: AxiosResponse<string> = yield call(UserSettingsApi.updateCountry, payload);
        yield put(setCountry(response.data));
    } catch (e) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* updateGenderRequest({ payload }: UpdateGenderActionInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        const response: AxiosResponse<string> = yield call(UserSettingsApi.updateGender, payload);
        yield put(setGender(response.data));
    } catch (e) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* updateLanguageRequest({ payload }: UpdateLanguageActionInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        const response: AxiosResponse<string> = yield call(UserSettingsApi.updateLanguage, payload);
        yield put(setLanguage(response.data));
    } catch (e) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* updateDirectRequest({ payload }: UpdateDirectActionInterface) {
    try {
        const response: AxiosResponse<boolean> = yield call(UserSettingsApi.updateDirectMessageRequests, payload);
        yield put(setDirect(response.data));
    } catch (e) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* updatePrivateProfileRequest({ payload }: UpdatePrivateProfileActionInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        const response: AxiosResponse<boolean> = yield call(UserSettingsApi.updatePrivateProfile, payload);
        yield put(setPrivateProfile(response.data));
    } catch (e) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* updateColorSchemeRequest({ payload }: UpdateColorSchemeActionInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        const response: AxiosResponse<string> = yield call(UserSettingsApi.updateColorScheme, payload);
        yield put(setColorScheme(response.data));
    } catch (e) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* updateBackgroundColorRequest({ payload }: UpdateBackgroundColorActionInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        const response: AxiosResponse<string> = yield call(UserSettingsApi.updateBackgroundColor, payload);
        yield put(setBackgroundColor(response.data));
    } catch (e) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* processUserToBlocklistRequest({ payload }: ProcessUserToBlocklistActionInterface) {
    try {
        const { data }: AxiosResponse<boolean> = yield call(BlockUserApi.processBlockList, payload.userId);
        yield put(setBlockedToTweetsState({ userId: payload.userId, tweetId: payload.tweetId!, isUserBlocked: data }));
        yield put(
            setBlockedUsersTweetState({ userId: payload.userId, tweetId: payload.tweetId!, isUserBlocked: data })
        );
        yield put(
            setBlockedToListTweetsState({ userId: payload.userId, tweetId: payload.tweetId!, isUserBlocked: data })
        );
        if (!payload?.hasProfileBlocked) {
            yield put(setBlocked({ isUserBlocked: data }));
        }
        yield put(setAddBlockedUser(payload.userId));
        yield put(setBlockUserDetail(data));
        yield put(setBlockedUser({ userId: payload.userId, isUserBlocked: data }));
        yield put(setBlockedUsersState({ userId: payload.userId, isUserBlocked: data }));
        yield put(setBlockUsersSearchState({ userId: payload.userId, isUserBlocked: data }));
        yield put(setBlockedToTweetState(data));
        yield put(setBlockedNotificationInfo(data));
        yield put(setBlockedTweetAdditionalInfo(data));
    } catch (e) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* processUserToMuteListRequest({ payload }: ProcessUserToMuteListActionInterface) {
    try {
        const { data }: AxiosResponse<boolean> = yield call(MuteUserApi.processMutedList, payload.userId);
        yield put(setMutedToTweetsState({ userId: payload.userId, tweetId: payload.tweetId!, isUserMuted: data }));
        yield put(setMutedUsersTweetState({ userId: payload.userId, tweetId: payload.tweetId!, isUserMuted: data }));
        yield put(setMutedToListTweetsState({ userId: payload.userId, tweetId: payload.tweetId!, isUserMuted: data }));
        yield put(setMuted(data));
        yield put(setMutedUser({ userId: payload.userId, isUserMuted: data }));
        yield put(setMutedUsersState({ userId: payload.userId, isUserMuted: data })); // TODO NOT NEEDED ???
        yield put(setMutedToTweetState(data));
        yield put(setMutedTweetAdditionalInfo(data));
    } catch (e) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* processFollowRequests({ payload }: ProcessFollowRequestActionInterface) {
    try {
        const { data }: AxiosResponse<UserProfileResponse> = yield call(
            FollowerUserApi.processFollowRequestToPrivateProfile,
            payload
        );
        yield put(setFollowRequestToUserProfile(data.isWaitingForApprove));
        yield put(setFollowRequestToUserDetail(data.isWaitingForApprove));
        yield put(setFollowRequestToUsers({ userId: data.id, isWaitingForApprove: data.isWaitingForApprove }));
        yield put(
            setFollowRequestToUsersSearchState({
                userId: data.id,
                isWaitingForApprove: data.isWaitingForApprove
            })
        );
        yield put(setFollowRequestToNotificationInfo(data.isWaitingForApprove));
    } catch (error) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* updatePhotoTaggingRequest({ payload }: UpdateBackgroundColorActionInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        yield call(UserSettingsApi.updatePhotoTagging, payload);
        yield put(setPhotoTagging(payload));
    } catch (e) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* userDeleteAccount({ payload }: DeleteUserAccountActionInterface) {
    try {
        yield put(setUserLoadingStatus(LoadingStatus.LOADING));
        const response: AxiosResponse<string> = yield call(UserSettingsApi.deleteUserAccount, payload);
        yield put(SetAccountDelete(true));
        if (response?.data) yield put(setOpenSnackBar({
            message: response.data
        }));
    } catch (e) {
        yield put(setUserLoadingStatus(LoadingStatus.ERROR));
    }
}

export function* userSaga() {
    yield takeLatest(UserActionsType.UPDATE_USER_DATA, updateUserDataRequest);
    yield takeLatest(UserActionsType.FETCH_SIGN_IN, fetchSignInRequest);
    yield takeLatest(UserActionsType.FETCH_SIGN_UP, fetchSignUpRequest);
    yield takeLatest(UserActionsType.FETCH_VERACITY_SCORE, fetchVeracityScore);
    yield takeLatest(UserActionsType.UPDATE_VERACITY_SCORE, updateVeracityScore);
    yield takeLatest(UserActionsType.FETCH_USER_DATA, fetchUserDataRequest);
    yield takeLatest(UserActionsType.FOLLOW_USER, processFollowUserRequest);
    yield takeLatest(UserActionsType.UNFOLLOW_USER, processFollowUserRequest);
    yield takeLatest(UserActionsType.START_USE_TWITTER, startUseTwitterRequest);
    yield takeLatest(UserActionsType.FETCH_PIN_TWEET, fetchPinTweetRequest);
    yield takeLatest(UserActionsType.FETCH_READ_MESSAGES, fetchReadMessagesRequest);
    yield takeLatest(UserActionsType.UPDATE_USERNAME, updateUsernameRequest);
    yield takeLatest(UserActionsType.UPDATE_EMAIL, updateEmailRequest);
    yield takeLatest(UserActionsType.UPDATE_PHONE, updatePhoneRequest);
    yield takeLatest(UserActionsType.UPDATE_COUNTRY, updateCountryRequest);
    yield takeLatest(UserActionsType.UPDATE_GENDER, updateGenderRequest);
    yield takeLatest(UserActionsType.UPDATE_LANGUAGE, updateLanguageRequest);
    yield takeLatest(UserActionsType.UPDATE_DIRECT, updateDirectRequest);
    yield takeLatest(UserActionsType.UPDATE_PRIVATE_PROFILE, updatePrivateProfileRequest);
    yield takeLatest(UserActionsType.UPDATE_COLOR_SCHEME, updateColorSchemeRequest);
    yield takeLatest(UserActionsType.UPDATE_BACKGROUND_COLOR, updateBackgroundColorRequest);
    yield takeLatest(UserActionsType.PROCESS_USER_TO_BLOCKLIST, processUserToBlocklistRequest);
    yield takeLatest(UserActionsType.PROCESS_USER_TO_MUTELIST, processUserToMuteListRequest);
    yield takeLatest(UserActionsType.PROCESS_FOLLOW_REQUEST, processFollowRequests);
    yield takeLatest(UserActionsType.UPDATE_PHOTO_TAGGING, updatePhotoTaggingRequest);
    yield takeLatest(UserActionsType.DELETE_ACCOUNT, userDeleteAccount);
}
